import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";

import { atom, useAtom } from "jotai";
import { atomFamily, useAtomValue } from "jotai/utils";

import { DataFile, DataFileCreationChain } from "interfaces";
import {
  useGetFetcher,
  useFetchEntity,
  useFetchMultipleEntities,
  useFetchPaginatedData,
} from "api";

import { apiUrls as urls } from "config";
import { useCompute } from "api";

import { useGetDataInsight } from "./insights";

export const fileColumns = atomFamily((fileId: string) => atom<string[]>([]));

export const useGetFileColumns = (fileId: string) =>
  useAtomValue(fileColumns(fileId));

export const deducedTypes = atomFamily((fileId: string) =>
  atom<DataFile.DeducedTypes>({})
);

export const useFetchDeducedColumnTypes = (
  dataFileId: string | undefined | null
) => {
  const url = `${urls.data}/${dataFileId}/${urls.data_column_types}`;
  const compute = useCompute<{}, DataFile.DeducedTypes>(url, true);
  const c = useCallback(() => compute(), [compute]);
  const resp = useQuery(url, c, { enabled: !!dataFileId });

  const [types, setTypes] = useAtom(deducedTypes(dataFileId));
  const { data: stats } = useGetDataInsight<{
    [col: string]: {
      [colType in DataFile.ColType]: DataFile.ColumnStatsInterface;
    };
  }>({
    fileId: dataFileId,
    name: "file_column_stats",
  });
  useEffect(() => {
    if (resp.data && dataFileId && stats) {
      if (
        Object.keys(types).sort().join("__@@__##__$$__") !==
        Object.keys(resp.data).sort().join("__@@__##__$$__")
      ) {
        // const nT = Object.fromEntries(
        //   Object.entries(resp.data).map(([column, colType]) => {
        //     if (colType !== "int") {
        //       return [column, colType];
        //     }
        //     const uniqueCounts = stats[column].numeric["unique-count"];
        //     return [column, uniqueCounts <= 10 ? "char" : "int"] as const;
        //   })
        // );
        // setTypes(nT);
        setTypes(resp.data);
      }
    }
  }, [resp, dataFileId, types, stats]);

  return types;
};

export const useFetchDataFile = (
  dataFileId: string | undefined,
  updateTypes?: boolean
) => {
  const resp = useFetchEntity<DataFile.DataFile>(urls.data, dataFileId);
  useFetchDeducedColumnTypes(updateTypes ? dataFileId : null);
  const data = resp.data;
  const [columns, setColumns] = useAtom(fileColumns(dataFileId));
  useEffect(() => {
    if (data) {
      const colsInFile = [...(data.columns || [])];
      if (colsInFile.length !== (columns?.length || 0)) {
        setColumns(colsInFile);
        return;
      }
      if (
        colsInFile.sort().join("__@@__##__") !==
        [...(columns || [])].sort().join("__@@__##__")
      ) {
        setColumns(colsInFile);
      }
    }
  }, [data, columns, setColumns]);

  return resp;
};

export const useFetchDataFiles = (files: string[]) => {
  return useFetchMultipleEntities<DataFile.DataFile>(urls.data, files);
};

export const useFetchDataFileListing = () => {
  return useFetchPaginatedData<DataFile.DataFile>(urls.data);
};

export const useCheckDataFileListingNonEmpty = () => {
  const [data] = useFetchDataFileListing();
  return data.flat().length >= 1;
};

export const useGetUpdateName = (dataFileId: string) => {
  const client = useQueryClient();
  const makeFetchRequest = useGetFetcher<DataFile.DataFile>();

  const submit = useCallback(
    async (name: string) => {
      const url = `${urls.data}/${dataFileId}`;

      const resp = await makeFetchRequest({
        url,
        method: "PUT",
        data: { name },
      });
      const data = client.getQueryData<DataFile.DataFile>([
        urls.data,
        dataFileId,
      ]);
      client.setQueryData<DataFile.DataFile>([urls.data, dataFileId], {
        ...data,
        name,
      });
      client.setQueryData<{ pages: DataFile.DataFile[][] }>(
        urls.data,
        (entities) => {
          if (!entities) {
            return { pages: [] };
          }
          const { pages } = entities;
          return {
            ...entities,
            pages: (pages || []).map((l) =>
              l.map((d) => (d.id !== dataFileId ? d : { ...d, name }))
            ),
          };
        }
      );
      return resp;
    },
    [dataFileId]
  );

  return submit;
};

export const useFetchCreationChain = (file: string) => {
  const makeFetchRequest = useGetFetcher<DataFileCreationChain>();

  const url = `${urls.data}/${file}/${urls.creation_chain}`;
  return useQuery(url, () => makeFetchRequest({ url, method: "GET" }));
};

export const useGenerateExport = (file: string, fileType: "csv" | "pq") => {
  const url = `${urls.data}/${file}/${
    fileType === "pq" ? urls.data_export_pq : urls.data_export_csv
  }`;
  const fetcher = useGetFetcher();

  return useCallback(async () => fetcher({ url, method: "POST" }), [
    url,
    fetcher,
  ]);
};
